import { App } from "@/App";
import { Button } from "@/components/Button/Button";
import { RefreshSvg } from "@/components/Icon/Icon";
import { POSTHOG_VISIBLE_KEY } from "@/constants/posthog";
import { store } from "@/store/store";
import { theme } from "@/themes/theme";
import { getAllApiDomains } from "@/utils/regionApi";
import { ThemeProvider } from "@emotion/react";
import { Box, CssBaseline, Typography } from "@mui/material";
import { PostHogProvider } from "@posthog/react";
import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";

const sentryDSN = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT;
const sentryProjectName = import.meta.env.VITE_SENTRY_PROJECT_NAME ?? "mesh-editor"; // TODO what is the name in sentry
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE_VERSION
  ? `${sentryProjectName}@${import.meta.env.VITE_SENTRY_RELEASE_VERSION}`
  : undefined;

Sentry.init({
  dsn: sentryDSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      blockAllMedia: true,
      maskAllText: true
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0,
  tracePropagationTargets: getAllApiDomains(),
  profilesSampleRate: 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Environment
  release: sentryRelease,
  environment: sentryEnvironment,
  debug: import.meta.env.NODE_ENV === "development",
  enabled: import.meta.env.VITE_ENABLE_SENTRY === "true"
});

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

const options = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  defaults: "2026-01-30",
  // Mask all text elements, excluding those with data-ph-visible="true".
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: "*",
    maskTextFn: (text: string, element: HTMLElement | undefined) => {
      if (element?.dataset[POSTHOG_VISIBLE_KEY] === "true") {
        return text;
      }
      return "*".repeat(text.trim().length);
    }
  }
} as const;

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <Sentry.ErrorBoundary
          fallback={({ resetError }) => {
            return (
              <Box
                component="div"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%"
                }}
              >
                <Button startIcon={<RefreshSvg color="inherit" />} onClick={() => resetError()}>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <Typography>Click here to reset!</Typography>
                </Button>
              </Box>
            );
          }}
          showDialog
        >
          <PostHogProvider apiKey={import.meta.env.VITE_POSTHOG_TOKEN} options={options}>
            <App />
          </PostHogProvider>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </Provider>
  </React.StrictMode>
);
